import { useState, useEffect, useMemo } from 'react'
import { apiClient, WeeklyShiftRequest, User } from '../lib/api'
import { useLiff } from '../hooks/useLiff'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

const DAY_LABELS = ['水曜日', '木曜日', '金曜日', '土曜日', '日曜日', '月曜日']

// 17:30〜翌2:00 の30分刻みの選択肢
function generateTimeOptions(): string[] {
  const times: string[] = []
  // 17:30〜23:30
  for (let h = 17; h <= 23; h++) {
    times.push(`${h}:30`)
    if (h < 23) times.push(`${h + 1}:00`)
    else times.push('0:00')
  }
  // 0:00, 0:30, 1:00, 1:30, 2:00
  times.push('0:30', '1:00', '1:30', '2:00')
  // dedupe and sort properly
  const unique = Array.from(new Set(times))
  // custom sort: 17-23 first, then 0-2
  return unique.sort((a, b) => {
    const ha = parseInt(a), hb = parseInt(b)
    const na = ha < 10 ? ha + 24 : ha
    const nb = hb < 10 ? hb + 24 : hb
    const ma = parseInt(a.split(':')[1]), mb = parseInt(b.split(':')[1])
    return (na * 60 + ma) - (nb * 60 + mb)
  })
}

const TIME_OPTIONS = generateTimeOptions()

interface DayEntry {
  enabled: boolean
  startTime: string
  endTime: string
  note: string
  date: string
  existingStatus?: string
  existingId?: number
}

// 次の水曜日(翌週の営業週開始)を計算
function getNextWednesday(): Date {
  const now = new Date()
  const day = now.getDay() // 0=Sun
  // 次の水曜日 = 今日 + (3 - day + 7) % 7、ただし今日が水曜でまだ月曜締切前なら今週
  // シンプルに: 現在の曜日から次の水曜日を計算
  let daysUntilWed = (3 - day + 7) % 7
  if (daysUntilWed === 0) daysUntilWed = 0 // 水曜日なら今日
  const wed = new Date(now)
  wed.setDate(now.getDate() + daysUntilWed)
  return wed
}

// 指定した水曜日から6日分(水〜月)の日付を取得
function getWeekDates(wednesday: Date): Date[] {
  const dates: Date[] = []
  for (let i = 0; i < 6; i++) {
    const d = new Date(wednesday)
    d.setDate(wednesday.getDate() + i)
    dates.push(d)
  }
  return dates
}

function formatDate(d: Date): string {
  return d.toISOString().split('T')[0]
}

// 締切 = 水曜日の前日の月曜日 (水の2日前)
function getDeadline(wednesday: Date): Date {
  const deadline = new Date(wednesday)
  deadline.setDate(wednesday.getDate() - 2)
  return deadline
}

export default function Shift() {
  const { accessToken, isReady } = useLiff()
  const [user, setUser] = useState<User | null>(null)
  const [userLoading, setUserLoading] = useState(true)
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [successMsg, setSuccessMsg] = useState('')
  const [entries, setEntries] = useState<DayEntry[]>([])
  const [weekOffset, setWeekOffset] = useState(0)

  const wednesday = useMemo(() => {
    const w = getNextWednesday()
    w.setDate(w.getDate() + weekOffset * 7)
    return w
  }, [weekOffset])

  const weekDates = useMemo(() => getWeekDates(wednesday), [wednesday])
  const deadline = useMemo(() => getDeadline(wednesday), [wednesday])
  const weekStart = formatDate(wednesday)
  const isPastDeadline = new Date() > new Date(formatDate(deadline) + 'T23:59:59')

  useEffect(() => {
    if (isReady && accessToken) {
      apiClient.setAccessToken(accessToken)
      loadUser()
    }
  }, [isReady, accessToken])

  const loadUser = async () => {
    try {
      const response = await apiClient.getMe()
      setUser(response.user)
    } catch (e) {
      console.error('Failed to load user:', e)
    } finally {
      setUserLoading(false)
    }
  }

  // 既存のリクエストをロード
  useEffect(() => {
    if (!user || userLoading) return
    loadExistingRequests()
  }, [user, userLoading, weekStart])

  const loadExistingRequests = async () => {
    setLoading(true)
    setError('')
    try {
      const data = await apiClient.getWeeklyShifts(weekStart)
      const existingByDate = new Map<string, WeeklyShiftRequest>()
      for (const r of data.requests) {
        existingByDate.set(r.date, r)
      }

      const newEntries: DayEntry[] = weekDates.map(d => {
        const dateStr = formatDate(d)
        const existing = existingByDate.get(dateStr)
        if (existing) {
          return {
            enabled: true,
            startTime: existing.start_time,
            endTime: existing.end_time,
            note: existing.note || '',
            date: dateStr,
            existingStatus: existing.status,
            existingId: existing.id,
          }
        }
        return {
          enabled: false,
          startTime: '17:30',
          endTime: '23:00',
          note: '',
          date: dateStr,
        }
      })
      setEntries(newEntries)
    } catch (e) {
      console.error('Failed to load shifts:', e)
      setError(e instanceof Error ? e.message : 'シフトデータの取得に失敗しました')
      // Initialize empty
      setEntries(weekDates.map(d => ({
        enabled: false, startTime: '17:30', endTime: '23:00', note: '', date: formatDate(d),
      })))
    }
    setLoading(false)
  }

  const updateEntry = (index: number, updates: Partial<DayEntry>) => {
    setEntries(prev => prev.map((e, i) => i === index ? { ...e, ...updates } : e))
  }

  const handleSubmit = async () => {
    if (submitting || isPastDeadline) return
    setSubmitting(true)
    setError('')
    setSuccessMsg('')

    const submitEntries = entries
      .filter(e => e.enabled && e.existingStatus !== 'approved')
      .map(e => ({
        date: e.date,
        startTime: e.startTime,
        endTime: e.endTime,
        note: e.note || undefined,
      }))

    try {
      await apiClient.submitWeeklyShifts(weekStart, submitEntries)
      setSuccessMsg('シフト希望を提出しました！')
      await loadExistingRequests()
    } catch (e) {
      console.error('Failed to submit:', e)
      setError('提出に失敗しました')
    }
    setSubmitting(false)
  }

  if (!isReady || userLoading) {
    return <LoadingSpinner message="読み込み中..." />
  }

  const enabledCount = entries.filter(e => e.enabled).length

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-md mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="text-center mb-4">
            <h1 className="text-xl font-bold text-gray-900">📅 シフト希望提出</h1>
            <p className="text-sm text-gray-600 mt-1">
              {wednesday.getMonth() + 1}月{wednesday.getDate()}日（水）〜 {weekDates[5].getMonth() + 1}月{weekDates[5].getDate()}日（月）
            </p>
          </div>

          {/* 締切表示 */}
          <div className={`text-center mb-4 p-3 rounded-lg ${isPastDeadline ? 'bg-red-50 border border-red-200' : 'bg-yellow-50 border border-yellow-200'}`}>
            <p className={`text-sm font-medium ${isPastDeadline ? 'text-red-700' : 'text-yellow-700'}`}>
              {isPastDeadline
                ? '⚠️ 提出期限を過ぎています'
                : `📮 提出期限: ${deadline.getMonth() + 1}月${deadline.getDate()}日（月）まで`}
            </p>
          </div>

          {/* 週移動 */}
          <div className="flex justify-between items-center mb-4">
            <button onClick={() => setWeekOffset(o => o - 1)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">← 前週</button>
            <button onClick={() => setWeekOffset(0)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">今週</button>
            <button onClick={() => setWeekOffset(o => o + 1)} className="px-3 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300">翌週 →</button>
          </div>

          <ErrorAlert error={error} onClose={() => setError('')} />
          {successMsg && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-700">✅ {successMsg}</p>
            </div>
          )}

          {loading ? (
            <LoadingSpinner message="読み込み中..." />
          ) : (
            <div className="space-y-3">
              {entries.map((entry, index) => {
                const d = weekDates[index]
                const dayLabel = DAY_LABELS[index]
                const isApproved = entry.existingStatus === 'approved'
                const isRejected = entry.existingStatus === 'rejected'

                return (
                  <div
                    key={entry.date}
                    className={`border rounded-lg p-4 transition-colors ${
                      isApproved ? 'border-green-400 bg-green-50' :
                      isRejected ? 'border-red-300 bg-red-50' :
                      entry.enabled ? 'border-blue-400 bg-blue-50' :
                      'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <label className="flex items-center space-x-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={entry.enabled}
                          onChange={(e) => updateEntry(index, { enabled: e.target.checked })}
                          disabled={isApproved}
                          className="w-5 h-5 text-blue-600 rounded"
                        />
                        <span className="font-bold text-gray-900">
                          {dayLabel}
                        </span>
                        <span className="text-sm text-gray-500">
                          ({d.getMonth() + 1}/{d.getDate()})
                        </span>
                      </label>
                      {isApproved && (
                        <span className="text-xs bg-green-200 text-green-800 px-2 py-1 rounded-full font-medium">確定</span>
                      )}
                      {isRejected && (
                        <span className="text-xs bg-red-200 text-red-800 px-2 py-1 rounded-full font-medium">不可</span>
                      )}
                    </div>

                    {entry.enabled && (
                      <div className="ml-7 space-y-2">
                        <div className="flex items-center space-x-2">
                          <select
                            value={entry.startTime}
                            onChange={(e) => updateEntry(index, { startTime: e.target.value })}
                            disabled={isApproved}
                            className="flex-1 p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            {TIME_OPTIONS.map(t => (
                              <option key={`s-${t}`} value={t}>{t}</option>
                            ))}
                          </select>
                          <span className="text-gray-600 font-medium">から</span>
                          <select
                            value={entry.endTime}
                            onChange={(e) => updateEntry(index, { endTime: e.target.value })}
                            disabled={isApproved}
                            className="flex-1 p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            {TIME_OPTIONS.map(t => (
                              <option key={`e-${t}`} value={t}>{t}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          <div className="mt-6">
            <button
              onClick={handleSubmit}
              disabled={submitting || isPastDeadline || enabledCount === 0}
              className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-blue-700 transition-colors"
            >
              {submitting ? '提出中...' : `シフト希望を提出（${enabledCount}日分）`}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
